import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PARTNERSHIP_STATUS_LABELS = {
    new: "New",
    contacted: "Contacted",
    in_talks: "In talks",
    closed_won: "Closed · won",
    closed_lost: "Closed · lost",
};
const PARTNERSHIP_STATUS_ORDER = ["new", "contacted", "in_talks", "closed_won", "closed_lost"];
const PARTNERSHIP_TYPE_LABELS = {
    event_sponsorship: "Event sponsorship",
    product_placement: "Product placement",
    welcome_bag: "Welcome bag",
    series_integration: "Series integration",
    social_content: "Social content",
    custom_activation: "Custom activation",
};

export default function PartnershipsPanel({ partners, onRefresh }) {
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("all");
    const [notes, setNotes] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);
    const [changingStatus, setChangingStatus] = useState(false);

    const visible = filter === "all" ? partners : partners.filter((p) => (p.status || "new") === filter);
    const counts = PARTNERSHIP_STATUS_ORDER.reduce((acc, s) => {
        acc[s] = partners.filter((p) => (p.status || "new") === s).length;
        return acc;
    }, {});

    const open = (p) => {
        setSelected(p);
        setNotes(p.admin_notes || "");
    };

    const setStatus = async (id, status) => {
        setChangingStatus(true);
        try {
            await api.patch(`/admin/partnerships/${id}/status?status=${status}`);
            toast.success(`Moved to ${PARTNERSHIP_STATUS_LABELS[status]}.`);
            setSelected((s) => (s && s.id === id ? { ...s, status } : s));
            onRefresh();
        } catch { toast.error("Status update failed."); }
        finally { setChangingStatus(false); }
    };

    const saveNotes = async () => {
        if (!selected) return;
        setSavingNotes(true);
        try {
            await api.patch(`/admin/partnerships/${selected.id}/notes`, { notes });
            toast.success("Notes saved.");
            setSelected((s) => s ? { ...s, admin_notes: notes } : s);
            onRefresh();
        } catch { toast.error("Notes save failed."); }
        finally { setSavingNotes(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this partnership inquiry? This can't be undone.")) return;
        try {
            await api.delete(`/admin/partnerships/${id}`);
            toast.success("Deleted.");
            setSelected(null);
            onRefresh();
        } catch { toast.error("Delete failed."); }
    };

    return (
        <div data-testid="partnerships-panel">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
                <div>
                    <p className="eyebrow">Partnerships</p>
                    <p className="text-sm text-[color:var(--cream-muted)] mt-2 max-w-2xl">
                        Every brand inquiry submitted from the Partnerships page. Click a card to read the full message, move it through your pipeline, and add private notes. A warm auto-acknowledgement fires the moment a brand submits — they hear from you within seconds.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="partnerships-status-filter">
                    <button
                        onClick={() => setFilter("all")}
                        className={`text-xs px-3 py-1 border ${filter === "all" ? "bg-[color:var(--gold)] text-[color:var(--warm-black)] border-[color:var(--gold)]" : "border-[color:var(--espresso)] text-[color:var(--cream-muted)]"}`}
                        data-testid="partnerships-filter-all"
                    >
                        {`All · ${partners.length}`}
                    </button>
                    {PARTNERSHIP_STATUS_ORDER.map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`text-xs px-3 py-1 border ${filter === s ? "bg-[color:var(--gold)] text-[color:var(--warm-black)] border-[color:var(--gold)]" : "border-[color:var(--espresso)] text-[color:var(--cream-muted)]"}`}
                            data-testid={`partnerships-filter-${s}`}
                        >
                            {`${PARTNERSHIP_STATUS_LABELS[s]} · ${counts[s] || 0}`}
                        </button>
                    ))}
                </div>
            </div>

            {visible.length === 0 ? (
                <div className="border border-dashed border-[color:var(--espresso)] p-10 text-center" data-testid="partnerships-empty">
                    <p className="text-sm text-[color:var(--cream-muted)]">
                        {partners.length === 0 ? "No partnership inquiries yet." : "No inquiries in this stage."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {visible.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => open(p)}
                            className="text-left border border-[color:var(--espresso)] p-5 hover:border-[color:var(--gold)] transition-colors"
                            data-testid={`partnership-card-${p.id}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="font-serif text-xl text-[color:var(--soft-cream)]">{p.company}</p>
                                    <p className="text-xs text-[color:var(--cream-muted)] mt-1">{PARTNERSHIP_TYPE_LABELS[p.partnership_type] || p.partnership_type}</p>
                                </div>
                                <span className={`text-[0.6rem] uppercase tracking-[0.18em] px-2 py-1 border ${(p.status || "new") === "new" ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--espresso)] text-[color:var(--cream-muted)]"}`}>
                                    {PARTNERSHIP_STATUS_LABELS[p.status || "new"]}
                                </span>
                            </div>
                            <div className="mt-4 text-xs text-[color:var(--cream-muted)] space-y-1">
                                <div>{p.contact_name} · <span className="text-[color:var(--gold)]">{p.email}</span></div>
                                {p.phone && <div>{p.phone}</div>}
                                {p.budget_range && <div>Budget: {p.budget_range}</div>}
                            </div>
                            <p className="mt-4 text-sm text-[color:var(--cream-muted)] line-clamp-3">{p.message}</p>
                            <p className="mt-4 text-[0.6rem] uppercase tracking-[0.18em] text-[color:var(--cream-muted)]">
                                {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                            </p>
                        </button>
                    ))}
                </div>
            )}

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl bg-[color:var(--warm-black)] border-[color:var(--gold)]" data-testid="partnership-detail">
                    {selected && (
                        <>
                            <DialogHeader>
                                <p className="eyebrow">Partnership inquiry</p>
                                <DialogTitle className="font-serif text-3xl text-[color:var(--soft-cream)]">{selected.company}</DialogTitle>
                                <p className="text-xs text-[color:var(--cream-muted)] mt-1">
                                    {PARTNERSHIP_TYPE_LABELS[selected.partnership_type] || selected.partnership_type} · submitted {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                                </p>
                            </DialogHeader>

                            <div className="space-y-4 text-sm">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-[color:var(--espresso)] p-4">
                                    <div>
                                        <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Contact</p>
                                        <p className="mt-1">{selected.contact_name}</p>
                                    </div>
                                    <div>
                                        <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Email</p>
                                        <a href={`mailto:${selected.email}`} className="mt-1 block text-[color:var(--gold)] link-underline" data-testid="partnership-email-link">{selected.email}</a>
                                    </div>
                                    {selected.phone && (
                                        <div>
                                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Phone</p>
                                            <a href={`tel:${selected.phone}`} className="mt-1 block text-[color:var(--gold)] link-underline">{selected.phone}</a>
                                        </div>
                                    )}
                                    {selected.budget_range && (
                                        <div>
                                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Budget range</p>
                                            <p className="mt-1">{selected.budget_range}</p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">Message</p>
                                    <div className="border border-[color:var(--espresso)] p-4 whitespace-pre-wrap text-[color:var(--soft-cream)]" data-testid="partnership-message">{selected.message}</div>
                                </div>

                                <div>
                                    <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">Pipeline</p>
                                    <div className="flex flex-wrap gap-2">
                                        {PARTNERSHIP_STATUS_ORDER.map((s) => {
                                            const active = (selected.status || "new") === s;
                                            return (
                                                <button
                                                    key={s}
                                                    disabled={changingStatus || active}
                                                    onClick={() => setStatus(selected.id, s)}
                                                    className={`text-xs px-3 py-1 border transition-colors disabled:opacity-60 ${active ? "bg-[color:var(--gold)] text-[color:var(--warm-black)] border-[color:var(--gold)]" : "border-[color:var(--espresso)] text-[color:var(--cream-muted)] hover:border-[color:var(--gold)]"}`}
                                                    data-testid={`partnership-status-${s}`}
                                                >
                                                    {PARTNERSHIP_STATUS_LABELS[s]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)] mb-2">Private notes (only you)</p>
                                    <textarea
                                        className="input-line w-full min-h-[100px]"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Add context: last touchpoint, next step, deal size…"
                                        data-testid="partnership-notes"
                                    />
                                </div>
                            </div>

                            <DialogFooter className="mt-4 flex flex-wrap gap-3 justify-between">
                                <button
                                    onClick={() => remove(selected.id)}
                                    className="text-xs px-3 py-2 border border-[color:var(--wine)] text-[color:var(--wine)] hover:bg-[color:var(--wine)] hover:text-[color:var(--soft-cream)] transition-colors"
                                    data-testid="partnership-delete"
                                >
                                    Delete inquiry
                                </button>
                                <div className="flex gap-3">
                                    <a
                                        href={`mailto:${selected.email}?subject=Mrs.Black(In)Love%20partnership%20—%20${encodeURIComponent(selected.company)}`}
                                        className="btn-gold text-xs"
                                        data-testid="partnership-reply"
                                    >
                                        Reply via email →
                                    </a>
                                    <button
                                        onClick={saveNotes}
                                        disabled={savingNotes}
                                        className="btn-gold btn-gold-solid text-xs disabled:opacity-40"
                                        data-testid="partnership-save-notes"
                                    >
                                        {savingNotes ? "Saving…" : "Save notes"}
                                    </button>
                                </div>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
